import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * TerminalWindow component for authentic terminal look.
 * Props:
 * - prompt: string (e.g. 'abdelhadi@blog:~$')
 * - promptAddon: ReactNode (optional addon to render after the prompt)
 * - children: ReactNode (output/content)
 * - className: string (extra classes)
 * - style: object (extra styles)
 * - command: string (current command input)
 * - onCommandChange: function (command change handler)
 * - onCommandSubmit: function (command submit handler)
 * - commandInputRef: ref (input ref)
 * - isInteractive: boolean (whether prompt is interactive)
 * - placeholder: string (placeholder text for the input)
 */
const TerminalWindow = ({ 
  prompt = 'abdelhadi@blog:~$', 
  promptAddon = null, 
  children, 
  className = '', 
  style = {},
  command = '',
  onCommandChange = null,
  onCommandSubmit = null,
  commandInputRef = null,
  isInteractive = false,
  placeholder = 'Type a command (help, ls, stat <id>, clear)'
}) => {
  const { getThemeClass } = useTheme();
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onCommandSubmit) {
      onCommandSubmit(e);
    }
  };

  return (
    <div
      className={`terminal-theme ${getThemeClass()} rounded-xl shadow-2xl border border-[#23272e] bg-[#181a1b] overflow-hidden relative ${className}`}
      style={style}
    >
      {/* Window controls */}
      <div className="flex items-center px-2 sm:px-4 py-1 sm:py-2 bg-[#23272e] border-b border-[#181a1b]">
        {isInteractive ? (
          <form onSubmit={handleSubmit} className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
            <span className="text-[#39c5bb] font-bold select-none text-xs sm:text-base whitespace-nowrap">{prompt}</span>
            <input
              ref={commandInputRef}
              type="text"
              className="bg-transparent outline-none border-none text-[#00ff66] font-mono flex-1 text-xs sm:text-base min-w-0 focus:ring-0 focus:outline-none"
              value={command}
              onChange={onCommandChange}
              autoFocus
              spellCheck={false}
              autoComplete="off"
              placeholder={placeholder}
              style={{minWidth:0}}
            />
            <span className="terminal-cursor bg-[#00ff66] ml-1 flex-shrink-0" style={{ animation: 'blink 1s steps(1) infinite', width: '0.5em', height: '1em', display: 'inline-block', verticalAlign: 'bottom' }}>&#9608;</span>
          </form>
        ) : (
          <>
            <span className="text-[#39c5bb] font-bold select-none text-xs sm:text-base">{prompt}</span>
            {promptAddon}
            {!promptAddon && (
              <span className="terminal-cursor bg-[#00ff66] ml-1 sm:ml-2 flex-shrink-0" style={{ animation: 'blink 1s steps(1) infinite', width: '0.5em', height: '1em', display: 'inline-block', verticalAlign: 'bottom' }}>&#9608;</span>
            )}
          </>
        )}
      </div>
      <div className="p-2 sm:p-4 md:p-8 font-mono text-[#e5e5e5] text-sm sm:text-base bg-[#181a1b] min-h-[200px]">
        {children}
      </div>
    </div>
  );
};

export default TerminalWindow; 